import { useEffect, useCallback } from 'react';

interface ShortcutConfig {
  key: string;
  meta?: boolean;  // ⌘ on Mac, Ctrl on Windows
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  action: () => void;
  description?: string;
}

export const useKeyboardShortcuts = (shortcuts: ShortcutConfig[]) => {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      shortcuts.forEach((shortcut) => {
        const metaMatch = shortcut.meta ? (e.metaKey || e.ctrlKey) : true;
        const ctrlMatch = shortcut.ctrl ? e.ctrlKey : true;
        const shiftMatch = shortcut.shift ? e.shiftKey : true;
        const altMatch = shortcut.alt ? e.altKey : true;
        const keyMatch = e.key.toLowerCase() === shortcut.key.toLowerCase();

        if (metaMatch && ctrlMatch && shiftMatch && altMatch && keyMatch) {
          e.preventDefault();
          shortcut.action();
        }
      });
    },
    [shortcuts]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
};

// Predefined keyboard shortcuts
export const KEYBOARD_SHORTCUTS = {
  COMMAND_PALETTE: { key: 'k', meta: true, description: 'Open command palette' },
  SEARCH: { key: 'f', meta: true, description: 'Universal search' },
  REFRESH: { key: 'r', meta: true, description: 'Refresh data' },
  EXPORT: { key: 'e', meta: true, description: 'Export data' },
  SETTINGS: { key: ',', meta: true, description: 'Open settings' },
  HELP: { key: '?', shift: true, description: 'Show help' },
  
  // Navigation
  NAV_TIMELINE: { key: '1', meta: true, description: 'Go to Timeline' },
  NAV_ANALYTICS: { key: '2', meta: true, description: 'Go to Analytics' },
  NAV_WORKLOG: { key: '3', meta: true, description: 'Go to Work Log' },
  NAV_SETTINGS: { key: '4', meta: true, description: 'Go to Settings' },
  
  // Actions
  NEW_EPIC: { key: 'n', meta: true, shift: true, description: 'Create new epic' },
  NEW_STORY: { key: 'n', meta: true, description: 'Create new story' },
  SAVE: { key: 's', meta: true, description: 'Save changes' },
  UNDO: { key: 'z', meta: true, description: 'Undo' },
  REDO: { key: 'z', meta: true, shift: true, description: 'Redo' },
};
